import { Link } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";

const deliverables = [
  {
    label: "Problem Validation",
    items: [
      "Market reality check",
      "Competitive landscape analysis",
      "ICP definition & refinement",
      "Customer interview synthesis",
    ],
  },
  {
    label: "Solution Architecture",
    items: [
      "MVP scope definition",
      "Tech stack decisions",
      "Build vs. buy analysis",
      "Integration mapping",
    ],
  },
  {
    label: "AI Integration Map",
    items: [
      "Where AI creates leverage vs. liability",
      "Agent architecture planning",
      "Data pipeline design",
      "Risk assessment",
    ],
  },
  {
    label: "Security Foundation",
    items: [
      "Data architecture & auth design",
      "Compliance baseline (SOC 2, HIPAA if applicable)",
      "Agent governance framework",
      "Threat model",
    ],
  },
  {
    label: "Launch Roadmap",
    items: [
      "30-60-90 day execution plan",
      "Milestone definitions",
      "Resource requirements",
      "Success metrics",
    ],
  },
];

const notIncluded = [
  "No pitch decks",
  "No vaporware",
  "No strategy docs that collect dust",
  "No handoffs to junior devs",
  "No meetings that should have been emails",
];

export default function DiscoverySprint() {
  return (
    <section id="discovery-sprint" aria-label="The Discovery Sprint" className="editorial-container section-spacing">
      <hr className="editorial-rule mb-16 lg:mb-20" />

      {/* Label */}
      <ScrollReveal>
        <p className="font-mono text-label uppercase tracking-[0.05em] text-accent">
          THE DISCOVERY SPRINT™
        </p>
      </ScrollReveal>

      {/* Headline */}
      <ScrollReveal stagger={1}>
        <h2 className="mt-6 max-w-3xl font-display text-section font-bold text-foreground md:text-section-md lg:text-section-lg">
          The Discovery Sprint™
        </h2>
      </ScrollReveal>

      {/* Subtitle */}
      <ScrollReveal stagger={2}>
        <p className="mt-4 max-w-2xl text-body-lg text-foreground-muted md:text-body-lg-md">
          A validated blueprint for your startup. 2&ndash;4 weeks.
        </p>
      </ScrollReveal>

      {/* Intro */}
      <ScrollReveal stagger={3}>
        <p className="mt-8 max-w-3xl text-foreground-muted md:text-body-md">
          I built the Discovery Sprint because every founder I worked with was
          making the same mistake: building before validating. They had
          assumptions, not data. Opinions, not signals. The Sprint replaces
          guesswork with a reality-tested foundation.
        </p>
      </ScrollReveal>

      {/* What You Get */}
      <ScrollReveal className="mt-16">
        <h3 className="font-display text-subsection font-semibold text-foreground md:text-subsection-md">
          What You Get
        </h3>
      </ScrollReveal>

      <div className="mt-10 grid gap-12 md:grid-cols-2 md:gap-x-16 md:gap-y-12">
        {deliverables.map((category, i) => (
          <ScrollReveal
            key={category.label}
            stagger={Math.min(i + 1, 5) as 1 | 2 | 3 | 4 | 5}
          >
            <div>
              <h4 className="font-mono text-label uppercase tracking-[0.05em] text-accent">
                {category.label}
              </h4>
              <ul className="mt-4 space-y-2">
                {category.items.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-accent"
                    >
                      ✓
                    </span>
                    <span className="text-foreground-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>
        ))}
      </div>

      {/* What's Not Included */}
      <ScrollReveal className="mt-16">
        <h3 className="font-display text-subsection font-semibold text-foreground md:text-subsection-md">
          What&rsquo;s Not Included
        </h3>
      </ScrollReveal>

      <ScrollReveal stagger={1} className="mt-6">
        <ul className="space-y-2">
          {notIncluded.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-foreground-faint"
              >
                ✗
              </span>
              <span className="text-foreground-faint">{item}</span>
            </li>
          ))}
        </ul>
      </ScrollReveal>

      {/* Outcome callout */}
      <ScrollReveal className="mt-16">
        <div className="border-l-2 border-accent bg-accent-subtle py-4 pl-6">
          <p className="text-body-lg font-medium text-foreground md:text-body-lg-md">
            You walk away with a validated direction and a buildable blueprint.
            Not another slide deck.
          </p>
        </div>
      </ScrollReveal>

      <ScrollReveal className="mt-10">
        <Link
          to="/discovery-sprint"
          className="group inline-flex items-center gap-2 font-body text-cta uppercase text-accent transition-all duration-300 hover:gap-4"
        >
          FULL DISCOVERY SPRINT DETAILS
          <span aria-hidden="true" className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">→</span>
        </Link>
      </ScrollReveal>
    </section>
  );
}
